import React, { useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, AlertOctagon, Eye, XCircle, CheckCircle2, Zap, TrendingUp, FileText, ShieldCheck, ArrowRight, ChevronDown, ChevronRight, Info, Database, Shield, VolumeX, Filter, Activity, BarChart3 } from 'lucide-react';
import { StatCard } from '../../../../shared/StatCard';
import { Badge } from '../../../../../components/ui/Badge';
import { Card } from '../../../../../components/ui/Card';
import { MODULE_CONFIG, PIPELINE_STAGES } from '../shared';
import type { GovernanceStats, ModuleHealth, FamilyHealth, FamilyTriageItem, UnifiedQuestionRow, SystemStats } from '../hooks/useGovernanceData';
import type { IssueDomain, CodeFrequencyReport, ShadowImpactReport } from '../../../../../governance';
import { getGuardGovernanceIssues } from '../../../../../practice/logic/guardGovernanceBridge';
interface OverviewDashboardProps {
  rows: UnifiedQuestionRow[];
  stats: GovernanceStats;
  systemStats: SystemStats;
  moduleHealth: ModuleHealth[];
  familyHealthByModule: Record<string, FamilyHealth[]>;
  questionTypeDistribution: Map<string, number>;
  visualTypeDistribution: Map<string, number>;
  topBlockers: UnifiedQuestionRow[];
  familyTriage: FamilyTriageItem[];
  pipelineIssueCounts: Record<IssueDomain, number> | null;
  codeFrequencyReport?: CodeFrequencyReport | null;
  shadowImpactReport?: ShadowImpactReport | null;
  isRunning: boolean;
  onNavigateToQuestion: (filter?: string) => void;
}
export function OverviewDashboard({
  rows,
  stats,
  systemStats,
  moduleHealth,
  familyHealthByModule,
  questionTypeDistribution,
  visualTypeDistribution,
  topBlockers,
  familyTriage,
  pipelineIssueCounts,
  codeFrequencyReport,
  shadowImpactReport,
  isRunning,
  onNavigateToQuestion
}: OverviewDashboardProps) {
  const isHealthy = stats.fail === 0 && stats.review === 0;
  const [expandedModules, setExpandedModules] = useState<Set<string>>(new Set());
  const [expandedSeeds, setExpandedSeeds] = useState<Set<string>>(new Set());
  const [showOnlyModulesWithIssues, setShowOnlyModulesWithIssues] = useState(true);
  const [sortUrgency, setSortUrgency] = useState(true);
  const [hideInfoCodes, setHideInfoCodes] = useState(false);
  const [hideNoiseCodes, setHideNoiseCodes] = useState(false);
  const [isDiagnoseOpen, setIsDiagnoseOpen] = useState(true);
  const [isSysteemOpen, setIsSysteemOpen] = useState(false);
  // P-9: Guard → Governance feedback data
  const guardSummary = useMemo(() => getGuardGovernanceIssues(), []);
  const toggleModule = (code: string) => {
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);else next.add(code);
      return next;
    });
  };
  const toggleSeed = (familyId: string) => {
    setExpandedSeeds(prev => {
      const next = new Set(prev);
      if (next.has(familyId)) next.delete(familyId);else next.add(familyId);
      return next;
    });
  };
  const triageByModule = useMemo(() => {
    const map = new Map<string, FamilyTriageItem[]>();
    for (const f of familyTriage) {
      if (!map.has(f.moduleCode)) map.set(f.moduleCode, []);
      map.get(f.moduleCode)!.push(f);
    }
    return map;
  }, [familyTriage]);
  const moduleRows = useMemo(() => {
    let mods = moduleHealth.filter(m => {
      if (showOnlyModulesWithIssues) return triageByModule.has(m.code);
      return true;
    });
    if (sortUrgency) {
      mods.sort((a, b) => {
        const aUrgency = triageByModule.get(a.code)?.reduce((sum, f) => sum + f.urgencyScore, 0) || 0;
        const bUrgency = triageByModule.get(b.code)?.reduce((sum, f) => sum + f.urgencyScore, 0) || 0;
        return bUrgency - aUrgency;
      });
    } else {
      mods.sort((a, b) => a.code.localeCompare(b.code));
    }
    return mods;
  }, [moduleHealth, triageByModule, showOnlyModulesWithIssues, sortUrgency]);
  // Total findings across all pipeline stages
  const totalFindings = pipelineIssueCounts ? Object.values(pipelineIssueCounts).reduce((sum, c) => sum + c, 0) : 0;
  // Filtered top codes for the noise widget (computed when report present)
  const filteredTopCodes = useMemo(() => {
    if (!codeFrequencyReport) return [];
    let list = codeFrequencyReport.entries;
    if (hideInfoCodes) list = list.filter(e => e.kind !== 'info');
    if (hideNoiseCodes) list = list.filter(e => !e.suspectedNoise);
    return list.slice(0, 10);
  }, [codeFrequencyReport, hideInfoCodes, hideNoiseCodes]);
  return <div className="relative max-w-7xl mx-auto pb-12" data-id="element-4710">
      {/* Vertical Connector Line for Narrative Flow */}
      <div className="absolute left-4 top-8 bottom-8 w-px bg-gray-200 hidden sm:block" data-id="element-4711" />

      <div className="space-y-12" data-id="element-4712">
        {/* ZONE A: Acties vandaag */}
        <section className="relative" data-id="element-4713">
          <header className="flex items-start gap-4 mb-6" data-id="element-4714">
            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-orange-100 text-orange-600 text-sm font-bold border-4 border-white shrink-0 hidden sm:flex shadow-sm" data-id="element-4715">
              1
            </div>
            <div className="pt-1" data-id="element-4716">
              <h2 className="text-lg font-bold text-gray-900" data-id="element-4717">
                Acties Vandaag
              </h2>
              <p className="text-sm text-gray-500 mt-0.5" data-id="element-4718">
                Wat vereist directe aandacht in de content pipeline?
              </p>
            </div>
          </header>

          <div className="sm:ml-12 space-y-6" data-id="element-4719">
            {/* Health Banner — question-level summary */}
            <div className={`flex items-center gap-4 px-5 py-4 rounded-2xl border shadow-sm transition-colors ${isHealthy ? 'bg-emerald-50/50 border-emerald-200' : stats.fail > 0 ? 'bg-red-50/50 border-red-200' : 'bg-amber-50/50 border-amber-200'}`} data-id="element-4720">
              <div className={`p-2 rounded-full flex-shrink-0 ${isHealthy ? 'bg-emerald-100 text-emerald-600' : stats.fail > 0 ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`} data-id="element-4721">
                {isHealthy ? <ShieldCheck className="w-6 h-6" data-id="element-4722" /> : stats.fail > 0 ? <XCircle className="w-6 h-6" data-id="element-4723" /> : <AlertTriangle className="w-6 h-6" data-id="element-4724" />}
              </div>
              <div className="flex-1 min-w-0" data-id="element-4725">
                <span className={`text-base font-semibold ${isHealthy ? 'text-emerald-900' : stats.fail > 0 ? 'text-red-900' : 'text-amber-900'}`} data-id="element-4726">
                  {isHealthy ? 'Alle vragen voldoen aan de kwaliteitseisen' : stats.fail > 0 ? `${stats.fail} ${stats.fail === 1 ? 'vraag' : 'vragen'} afgekeurd` : 'Aandachtspunten gevonden'}
                </span>
                {!isHealthy && <div className={`text-sm mt-0.5 ${stats.fail > 0 ? 'text-red-700/80' : 'text-amber-700/80'}`} data-id="element-4727">
                    {[stats.review > 0 && `${stats.review} vereisen review`, totalFindings > 0 && `${totalFindings} bevindingen totaal`].filter(Boolean).join(' · ')}
                  </div>}
              </div>
            </div>

            <Card className="p-0 shadow-sm border-gray-200/60 overflow-hidden" data-id="element-4728">
              <div className="p-5 border-b border-gray-100 bg-white flex flex-col sm:flex-row sm:items-start justify-between gap-4" data-id="element-4729">
                <div data-id="element-4730">
                  <h3 className="text-sm font-semibold text-gray-900" data-id="element-4731">
                    Werklijst — Module → Seed → Vraag
                  </h3>
                  <p className="text-[11px] text-gray-500 mt-1 max-w-2xl" data-id="element-4732">
                    Open een module om de probleem-seeds te zien. Open een seed
                    om de afgekeurde vragen te zien. Klik op een vraag voor
                    details.
                  </p>
                </div>
                <div className="flex items-center gap-3 flex-shrink-0" data-id="element-4733">
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer hover:text-gray-900" data-id="element-4734">
                    <input type="checkbox" checked={showOnlyModulesWithIssues} onChange={e => setShowOnlyModulesWithIssues(e.target.checked)} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-4735" />
                    Alleen issues
                  </label>
                  <select value={sortUrgency ? 'urgency' : 'alpha'} onChange={e => setSortUrgency(e.target.value === 'urgency')} className="text-xs border-gray-200 rounded-md text-gray-600 py-1 pl-2 pr-6 focus:ring-blue-500 focus:border-blue-500" data-id="element-4736">
                    <option value="urgency" data-id="element-4737">Sorteer: Urgentie</option>
                    <option value="alpha" data-id="element-4738">Sorteer: A-Z</option>
                  </select>
                </div>
              </div>

              <div className="divide-y divide-gray-100" data-id="element-4739">
                {moduleRows.length === 0 ? <div className="p-8 text-center text-gray-500 text-sm" data-id="element-4740">
                    Geen modules gevonden met de huidige filters.
                  </div> : moduleRows.map(mod => {
                const isExpanded = expandedModules.has(mod.code);
                const modTriage = triageByModule.get(mod.code) || [];
                const config = MODULE_CONFIG[mod.code];
                const bgClass = config?.bgClass ?? 'bg-gray-400';
                const criticalCount = modTriage.reduce((sum, f) => sum + f.criticalCount, 0);
                const blockerCount = modTriage.reduce((sum, f) => sum + f.blockerCount, 0);
                const reviewCount = modTriage.reduce((sum, f) => sum + f.reviewCount, 0);
                const worstPct = modTriage.length > 0 ? Math.min(...modTriage.map(f => f.avgScore)) : 100;
                return <div key={mod.code} className="bg-white" data-id="element-4741">
                        <div className="flex items-center justify-between p-3 hover:bg-gray-50 cursor-pointer transition-colors" onClick={() => toggleModule(mod.code)} data-id="element-4742">
                          <div className="flex items-center gap-3" data-id="element-4743">
                            <div className={`w-2.5 h-2.5 rounded-full ${bgClass}`} data-id="element-4744" />
                            <Badge variant="neutral" className="font-mono font-bold" data-id="element-4745">
                              {mod.code}
                            </Badge>
                            <span className="text-sm text-gray-600 hidden sm:inline" data-id="element-4746">
                              {mod.name}
                            </span>
                          </div>
                          <div className="flex items-center gap-4" data-id="element-4747">
                            <div className="hidden md:flex items-center gap-1.5" data-id="element-4748">
                              {criticalCount > 0 && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded" data-id="element-4749">
                                  {criticalCount} critical
                                </span>}
                              {blockerCount > 0 && <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded" data-id="element-4750">
                                  {blockerCount} blocker
                                </span>}
                              {reviewCount > 0 && <span className="text-[10px] font-semibold text-yellow-700 bg-yellow-50 border border-yellow-100 px-1.5 py-0.5 rounded" data-id="element-4751">
                                  {reviewCount} review
                                </span>}
                            </div>
                            <div className="text-right text-xs text-gray-500 w-32" data-id="element-4752">
                              {modTriage.length > 0 ? <>
                                  <span className="font-medium text-gray-900" data-id="element-4753">
                                    {modTriage.length}
                                  </span>{' '}
                                  van{' '}
                                  {familyHealthByModule[mod.code]?.length || 0}{' '}
                                  seeds
                                  <div className="text-[10px] text-red-500" data-id="element-4754">
                                    worst: {worstPct}%
                                  </div>
                                </> : <span className="text-emerald-600 font-medium" data-id="element-4755">
                                  Alle{' '}
                                  {familyHealthByModule[mod.code]?.length || 0}{' '}
                                  seeds OK
                                </span>}
                            </div>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} data-id="element-4756" />
                          </div>
                        </div>

                        <AnimatePresence data-id="element-4757">
                          {isExpanded && modTriage.length > 0 && <motion.div initial={{
                      height: 0,
                      opacity: 0
                    }} animate={{
                      height: 'auto',
                      opacity: 1
                    }} exit={{
                      height: 0,
                      opacity: 0
                    }} transition={{
                      duration: 0.2
                    }} className="overflow-hidden bg-gray-50/30 border-t border-gray-100" data-id="element-4758">
                              <div className="divide-y divide-gray-100/50" data-id="element-4759">
                                {modTriage.map((fam, idx) => {
                          const isSeedExpanded = expandedSeeds.has(fam.familyId);
                          return <div key={fam.familyId} data-id="element-4760">
                                      <div className="flex items-center justify-between p-2.5 pl-4 sm:pl-12 hover:bg-orange-50/40 transition-colors group relative" data-id="element-4761">
                                        <div className="hidden sm:block absolute left-6 top-0 bottom-0 w-px bg-gray-200" data-id="element-4762" />
                                        <div className="hidden sm:block absolute left-6 top-1/2 w-4 h-px bg-gray-200" data-id="element-4763" />

                                        <div className="flex items-center gap-3 min-w-0" data-id="element-4764">
                                          <div className="flex-shrink-0 w-5 h-5 rounded-full bg-white border border-gray-200 text-[9px] font-bold text-gray-500 flex items-center justify-center shadow-sm" data-id="element-4765">
                                            {idx + 1}
                                          </div>
                                          <div className="min-w-0" data-id="element-4766">
                                            <div className="flex items-center gap-2" data-id="element-4767">
                                              <span className="font-mono text-xs font-bold text-gray-900" data-id="element-4768">
                                                {fam.familyId}
                                              </span>
                                              <div className="hidden lg:flex items-center gap-1" data-id="element-4769">
                                                {fam.topIssueCodes.slice(0, 2).map(code => <span key={code} className="text-[9px] font-mono text-gray-500 bg-white border border-gray-200 px-1 rounded truncate max-w-[120px]" title={code} data-id="element-4770">
                                                      {code}
                                                    </span>)}
                                              </div>
                                            </div>
                                            <div className="text-[10px] text-gray-500 mt-0.5" data-id="element-4771">
                                              {fam.total} vragen ·{' '}
                                              <span className="text-emerald-600" data-id="element-4772">
                                                {fam.pass} pass
                                              </span>
                                              {fam.fail > 0 && <span className="text-red-600" data-id="element-4773">
                                                  {' '}
                                                  · {fam.fail} fail
                                                </span>}
                                              {fam.review > 0 && <span className="text-amber-600" data-id="element-4774">
                                                  {' '}
                                                  · {fam.review} review
                                                </span>}
                                            </div>
                                          </div>
                                        </div>

                                        <div className="flex items-center gap-4 flex-shrink-0" data-id="element-4775">
                                          <div className="hidden md:flex items-center gap-1" data-id="element-4776">
                                            {fam.criticalCount > 0 && <span className="text-[10px] font-semibold text-red-700 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded" title="Kritiek" data-id="element-4777">
                                                <AlertOctagon className="w-3 h-3 inline mr-0.5" data-id="element-4778" />
                                                {fam.criticalCount}
                                              </span>}
                                            {fam.blockerCount > 0 && <span className="text-[10px] font-semibold text-orange-700 bg-orange-50 border border-orange-100 px-1.5 py-0.5 rounded" title="Blocker" data-id="element-4779">
                                                <XCircle className="w-3 h-3 inline mr-0.5" data-id="element-4780" />
                                                {fam.blockerCount}
                                              </span>}
                                            {fam.reviewCount > 0 && <span className="text-[10px] font-semibold text-yellow-700 bg-yellow-50 border border-yellow-100 px-1.5 py-0.5 rounded" title="Review" data-id="element-4781">
                                                <Eye className="w-3 h-3 inline mr-0.5" data-id="element-4782" />
                                                {fam.reviewCount}
                                              </span>}
                                          </div>
                                          <div className="w-10 text-right hidden sm:block" data-id="element-4783">
                                            <span className={`text-xs font-bold ${fam.avgScore >= 80 ? 'text-emerald-600' : fam.avgScore >= 60 ? 'text-amber-600' : 'text-red-600'}`} data-id="element-4784">
                                              {fam.avgScore}%
                                            </span>
                                          </div>
                                          <div className="flex items-center gap-2" data-id="element-4785">
                                            <button onClick={() => onNavigateToQuestion(fam.familyId)} className="text-[10px] font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 px-2 py-1 rounded transition-colors hidden sm:block" data-id="element-4786">
                                              Open in Vragen →
                                            </button>
                                            <button onClick={() => toggleSeed(fam.familyId)} className="p-1 rounded hover:bg-gray-200 transition-colors" data-id="element-4787">
                                              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isSeedExpanded ? 'rotate-180' : ''}`} data-id="element-4788" />
                                            </button>
                                          </div>
                                        </div>
                                      </div>

                                      <AnimatePresence data-id="element-4789">
                                        {isSeedExpanded && <motion.div initial={{
                                height: 0,
                                opacity: 0
                              }} animate={{
                                height: 'auto',
                                opacity: 1
                              }} exit={{
                                height: 0,
                                opacity: 0
                              }} transition={{
                                duration: 0.2
                              }} className="overflow-hidden bg-white border-y border-gray-100/50" data-id="element-4790">
                                            <div className="py-2 space-y-1 relative" data-id="element-4791">
                                              <div className="hidden sm:block absolute left-6 top-0 bottom-0 w-px bg-gray-200" data-id="element-4792" />

                                              {(() => {
                                    const qs = rows.filter(r => r.familyId === fam.familyId && (r.verdict !== 'PASS' || r.blockerCount > 0 || r.warningCount > 0));
                                    const displayQs = qs.slice(0, 5);
                                    return <>
                                                    {displayQs.map(q => {
                                        const firstBlocker = q.pipelineIssues.find(i => i.kind === 'blocker' || i.kind === 'critical') || q.pipelineIssues.find(i => i.kind === 'coach');
                                        return <div key={q.questionId} onClick={() => onNavigateToQuestion(q.questionId)} className="ml-4 sm:ml-20 mr-4 p-2 rounded-lg border border-gray-100 hover:border-red-200 hover:bg-red-50/40 cursor-pointer transition-colors flex items-center gap-3 group" data-id="element-4793">
                                                          <div className="w-1.5 h-1.5 rounded-full bg-red-400 flex-shrink-0" data-id="element-4794" />
                                                          <div className="min-w-0 flex-1" data-id="element-4795">
                                                            <div className="flex items-center gap-2 mb-0.5" data-id="element-4796">
                                                              <span className="font-mono text-[11px] font-bold text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-4797">
                                                                {q.questionId}
                                                              </span>
                                                              <Badge variant={q.verdict === 'FAIL' ? 'danger' : 'warning'} size="sm" data-id="element-4798">
                                                                {q.verdict}
                                                              </Badge>
                                                              <span className="text-[9px] text-gray-500 font-medium hidden sm:inline" data-id="element-4799">
                                                                {q.blockerCount}{' '}
                                                                blocker
                                                                {q.blockerCount !== 1 ? 's' : ''}
                                                                {q.warningCount > 0 && ` · ${q.warningCount} waarschuwing${q.warningCount !== 1 ? 'en' : ''}`}
                                                              </span>
                                                            </div>
                                                            {firstBlocker && <p className="text-[11px] text-gray-600 truncate" data-id="element-4800">
                                                                <span className="font-mono font-semibold text-[9px] bg-gray-100 px-1 py-0.5 rounded text-gray-500 mr-1.5" data-id="element-4801">
                                                                  {firstBlocker.code}
                                                                </span>
                                                                {firstBlocker.message}
                                                              </p>}
                                                          </div>
                                                          <ArrowRight className="w-3 h-3 text-gray-300 group-hover:text-red-500 group-hover:translate-x-0.5 transition-all flex-shrink-0" data-id="element-4802" />
                                                        </div>;
                                      })}
                                                    {qs.length > 5 && <div className="ml-4 sm:ml-20 mr-4 pt-1" data-id="element-4803">
                                                        <button onClick={() => onNavigateToQuestion(fam.familyId)} className="text-[10px] font-medium text-gray-500 hover:text-blue-600 flex items-center gap-1" data-id="element-4804">
                                                          + {qs.length - 5} meer
                                                          vragen — open seed in
                                                          Vragen tab{' '}
                                                          <ArrowRight className="w-3 h-3" data-id="element-4805" />
                                                        </button>
                                                      </div>}
                                                  </>;
                                  })()}
                                            </div>
                                          </motion.div>}
                                      </AnimatePresence>
                                    </div>;
                        })}
                              </div>
                            </motion.div>}
                        </AnimatePresence>
                      </div>;
              })}
              </div>
            </Card>
          </div>
        </section>

        {/* ZONE B: Diagnose */}
        <section className="relative" data-id="element-4806">
          <header className="flex items-start gap-4 mb-6" data-id="element-4807">
            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-600 text-sm font-bold border-4 border-white shrink-0 hidden sm:flex shadow-sm" data-id="element-4808">
              2
            </div>
            <div className="pt-1 w-full" data-id="element-4809">
              <button onClick={() => setIsDiagnoseOpen(!isDiagnoseOpen)} className="flex items-center justify-between w-full text-left group" data-id="element-4810">
                <div data-id="element-4811">
                  <h2 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors" data-id="element-4812">
                    Diagnose
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5" data-id="element-4813">
                    Waar in de pipeline ontstaan de bevindingen?
                  </p>
                </div>
                <div className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-4814">
                  {isDiagnoseOpen ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-4815" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-4816" />}
                </div>
              </button>
            </div>
          </header>

          <AnimatePresence initial={false} data-id="element-4817">
            {isDiagnoseOpen && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="sm:ml-12 overflow-hidden" data-id="element-4818">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 pb-2" data-id="element-4819">
                  <div className="space-y-6" data-id="element-4820">
                    {/* Pipeline Stages */}
                    <Card className="p-5 shadow-sm border-gray-200/60" data-id="element-4821">
                      <div className="mb-5" data-id="element-4822">
                        <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4823">
                          <Activity className="w-4 h-4 text-blue-500" data-id="element-4824" />{' '}
                          Pipeline Fasen
                        </h3>
                        <p className="text-[11px] text-gray-500 mt-1" data-id="element-4825">
                          Aantal individuele bevindingen per controle-fase.
                        </p>
                      </div>
                      <div className="space-y-2.5" data-id="element-4826">
                        {PIPELINE_STAGES.map(stage => {
                      const count = pipelineIssueCounts?.[stage.domain] ?? 0;
                      const status = !pipelineIssueCounts ? 'pending' : count === 0 ? 'ok' : count <= 5 ? 'warning' : 'error';
                      const statusConfig = {
                        ok: {
                          dot: 'bg-emerald-500',
                          border: 'border-emerald-100',
                          bg: 'bg-emerald-50/30'
                        },
                        warning: {
                          dot: 'bg-amber-500',
                          border: 'border-amber-100',
                          bg: 'bg-amber-50/30'
                        },
                        error: {
                          dot: 'bg-red-500',
                          border: 'border-red-100',
                          bg: 'bg-red-50/30'
                        },
                        pending: {
                          dot: 'bg-gray-300',
                          border: 'border-gray-100',
                          bg: 'bg-gray-50/50'
                        }
                      }[status];
                      return <div key={stage.name} className={`flex items-center justify-between p-3 rounded-xl border transition-colors ${statusConfig.bg} ${statusConfig.border}`} data-id="element-4827">
                              <div className="flex items-center gap-3" data-id="element-4828">
                                <div className={`w-2 h-2 rounded-full shadow-sm ${statusConfig.dot}`} data-id="element-4829" />
                                <span className="text-sm font-medium text-gray-700" data-id="element-4830">
                                  {stage.label}
                                </span>
                              </div>
                              {status !== 'pending' && <Badge variant={status === 'ok' ? 'success' : status === 'warning' ? 'warning' : 'danger'} size="sm" data-id="element-4831">
                                  {count === 0 ? '0 bevindingen' : `${count} bevindingen`}
                                </Badge>}
                            </div>;
                    })}
                      </div>
                    </Card>

                    {/* P-9: Guard Runtime Rejections */}
                    <Card className="p-5 shadow-sm border-gray-200/60" data-id="element-4832">
                      <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2" data-id="element-4833">
                        <Shield className="w-4 h-4 text-orange-500" data-id="element-4834" /> Runtime
                        Guard
                      </h3>
                      <p className="text-[11px] text-gray-500 mb-5" data-id="element-4835">
                        Vragen afgewezen door de pedagogische guard tijdens live
                        oefensessies.
                      </p>

                      {guardSummary.totalRejections === 0 ? <div className="bg-emerald-50/50 rounded-xl p-5 text-center border border-emerald-100" data-id="element-4836">
                          <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-2" data-id="element-4837" />
                          <p className="text-xs font-medium text-emerald-800" data-id="element-4838">
                            Geen runtime afwijzingen
                          </p>
                        </div> : <div className="space-y-4" data-id="element-4839">
                          <div className="grid grid-cols-2 gap-3" data-id="element-4840">
                            <div className="bg-red-50/50 rounded-xl p-3 border border-red-100 text-center" data-id="element-4841">
                              <p className="text-xl font-bold text-red-700" data-id="element-4842">
                                {guardSummary.totalAffectedQuestions}
                              </p>
                              <p className="text-[10px] font-medium text-red-600/80 uppercase tracking-wider mt-0.5" data-id="element-4843">
                                Vragen
                              </p>
                            </div>
                            <div className="bg-orange-50/50 rounded-xl p-3 border border-orange-100 text-center" data-id="element-4844">
                              <p className="text-xl font-bold text-orange-700" data-id="element-4845">
                                {guardSummary.totalRejections}
                              </p>
                              <p className="text-[10px] font-medium text-orange-600/80 uppercase tracking-wider mt-0.5" data-id="element-4846">
                                Afwijzingen
                              </p>
                            </div>
                          </div>

                          {guardSummary.reasonBreakdown.length > 0 && <div className="space-y-2" data-id="element-4847">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-4848">
                                Top redenen
                              </p>
                              {guardSummary.reasonBreakdown.slice(0, 3).map(r => <div key={r.reason} className="flex items-start gap-2 text-[11px]" data-id="element-4849">
                                    <span className="font-mono text-red-600 font-semibold flex-shrink-0 bg-red-50 px-1 rounded" data-id="element-4850">
                                      {r.count}×
                                    </span>
                                    <span className="text-gray-600 leading-tight pt-0.5" data-id="element-4851">
                                      {r.description}
                                    </span>
                                  </div>)}
                            </div>}

                          {guardSummary.issues.length > 0 && <div className="space-y-2" data-id="element-4852">
                              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider" data-id="element-4853">
                                Meest afgewezen
                              </p>
                              {guardSummary.issues.slice(0, 3).map(issue => <div key={issue.questionId} className="flex items-center justify-between text-[10px] bg-gray-50 px-2 py-1.5 rounded-lg border border-gray-100" data-id="element-4854">
                                  <span className="font-mono font-medium text-gray-700 truncate mr-2" title={issue.questionId} data-id="element-4855">
                                    {issue.questionId}
                                  </span>
                                  <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-4856">
                                    {issue.fatalCount > 0 && <span className="text-red-600 font-bold bg-red-100/50 px-1.5 rounded" data-id="element-4857">
                                        {issue.fatalCount}F
                                      </span>}
                                    {issue.softCount > 0 && <span className="text-amber-600 font-bold bg-amber-100/50 px-1.5 rounded" data-id="element-4858">
                                        {issue.softCount}S
                                      </span>}
                                  </div>
                                </div>)}
                            </div>}

                          {guardSummary.dataRange && <p className="text-[10px] text-gray-400 pt-2 border-t border-gray-100 text-center" data-id="element-4859">
                              Data:{' '}
                              {new Date(guardSummary.dataRange.from).toLocaleDateString('nl-NL')}{' '}
                              —{' '}
                              {new Date(guardSummary.dataRange.to).toLocaleDateString('nl-NL')}
                            </p>}
                        </div>}
                    </Card>
                  </div>

                  {/* Top Noise Sources (Meest Voorkomende Codes) */}
                  <div className="space-y-6" data-id="element-4860">
                    {codeFrequencyReport && codeFrequencyReport.totalIssues > 0 && <Card className="p-5 shadow-sm border-gray-200/60 flex flex-col h-full" data-id="element-4861">
                          <div className="flex items-start justify-between mb-5 gap-4" data-id="element-4862">
                            <div className="min-w-0" data-id="element-4863">
                              <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-4864">
                                <BarChart3 className="w-4 h-4 text-purple-500" data-id="element-4865" />{' '}
                                Meest Voorkomende Codes
                              </h3>
                              <p className="text-[11px] text-gray-500 mt-1" data-id="element-4866">
                                Welke validator-codes domineren de{' '}
                                <span className="font-semibold text-gray-700" data-id="element-4867">
                                  {codeFrequencyReport.totalIssues.toLocaleString('nl-NL')}
                                </span>{' '}
                                bevindingen? Helpt bij het identificeren van
                                ruis.
                              </p>
                            </div>
                            <div className="flex flex-col gap-1.5 flex-shrink-0" data-id="element-4868">
                              <button onClick={() => setHideInfoCodes(v => !v)} className={`text-[10px] font-medium px-2 py-1 rounded-md border transition-all ${hideInfoCodes ? 'bg-slate-100 border-slate-300 text-slate-700 shadow-inner' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`} title="Verberg info-bevindingen (severity ≤ TRACE)" data-id="element-4869">
                                <Filter className="w-3 h-3 inline mr-1" data-id="element-4870" />
                                {hideInfoCodes ? 'Info verborgen' : 'Verberg info'}
                              </button>
                              <button onClick={() => setHideNoiseCodes(v => !v)} className={`text-[10px] font-medium px-2 py-1 rounded-md border transition-all ${hideNoiseCodes ? 'bg-amber-50 border-amber-300 text-amber-800 shadow-inner' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'}`} title="Verberg codes met verdacht-ruis vlag (>20% van vragen of TRACE-severity)" data-id="element-4871">
                                <VolumeX className="w-3 h-3 inline mr-1" data-id="element-4872" />
                                {hideNoiseCodes ? 'Ruis verborgen' : 'Verberg ruis'}
                              </button>
                            </div>
                          </div>

                          <div className="flex-1" data-id="element-4873">
                            {filteredTopCodes.length === 0 ? <div className="bg-emerald-50/50 border border-emerald-100 rounded-xl p-6 text-center h-full flex flex-col items-center justify-center" data-id="element-4874">
                                <div className="w-10 h-10 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-3" data-id="element-4875">
                                  <CheckCircle2 className="w-5 h-5" data-id="element-4876" />
                                </div>
                                <p className="text-xs text-emerald-800 font-medium" data-id="element-4877">
                                  Geen codes om weer te geven met huidige
                                  filters.
                                </p>
                              </div> : <div className="space-y-2" data-id="element-4878">
                                {filteredTopCodes.map((entry, idx) => {
                        const kindColor = entry.kind === 'blocker' ? 'danger' : entry.kind === 'coach' ? 'warning' : 'neutral';
                        return <div key={entry.code} className={`flex items-center gap-3 p-3 rounded-xl border transition-colors ${entry.suspectedNoise ? 'bg-amber-50/30 border-amber-100 hover:bg-amber-50/60' : 'bg-gray-50/40 border-gray-100 hover:bg-gray-50/80'}`} data-id="element-4879">
                                      <span className="text-[10px] font-bold text-gray-400 w-5 flex-shrink-0 text-center" data-id="element-4880">
                                        {idx + 1}
                                      </span>
                                      <div className="min-w-0 flex-1" data-id="element-4881">
                                        <div className="flex items-center gap-2 flex-wrap mb-1" data-id="element-4882">
                                          <span className="font-mono text-[11px] font-bold text-gray-900 truncate" title={entry.code} data-id="element-4883">
                                            {entry.code}
                                          </span>
                                          <Badge variant={kindColor} size="sm" data-id="element-4884">
                                            {entry.kind}
                                          </Badge>
                                          <span className="text-[9px] text-gray-400 font-mono uppercase tracking-wider" data-id="element-4885">
                                            {entry.domain}
                                          </span>
                                          {entry.suspectedNoise && <span className="text-[9px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded" title="Verdacht ruis — komt op >20% van vragen voor of severity ≤ TRACE" data-id="element-4886">
                                              ruis?
                                            </span>}
                                        </div>
                                        <p className="text-[10px] text-gray-500" data-id="element-4887">
                                          {entry.questionsAffected.toLocaleString('nl-NL')}{' '}
                                          vragen ·{' '}
                                          <span className="font-medium text-gray-700" data-id="element-4888">
                                            {entry.percentOfQuestions}%
                                          </span>{' '}
                                          · max ernst {entry.maxSeverity} · gem.{' '}
                                          {entry.avgSeverity}
                                        </p>
                                        {entry.reasonBreakdown && entry.reasonBreakdown.length > 0 && <div className="mt-2 flex flex-wrap gap-1.5" title="Rollup-code — opgesplitst per metadata.reason" data-id="element-4889">
                                              {entry.reasonBreakdown.map(r => <span key={r.reason} className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md bg-white border border-gray-200 text-[10px] text-gray-700 font-mono shadow-sm" data-id="element-4890">
                                                    <span className="text-gray-500" data-id="element-4891">
                                                      {r.reason}
                                                    </span>
                                                    <span className="font-bold text-gray-900" data-id="element-4892">
                                                      {r.count.toLocaleString('nl-NL')}
                                                    </span>
                                                    <span className="text-gray-400" data-id="element-4893">
                                                      ({r.percentOfCode}%)
                                                    </span>
                                                  </span>)}
                                            </div>}
                                      </div>
                                      <div className="flex flex-col items-end flex-shrink-0 pl-2 border-l border-gray-200/60" data-id="element-4894">
                                        <span className="text-lg font-bold text-gray-900 leading-none" data-id="element-4895">
                                          {entry.count.toLocaleString('nl-NL')}
                                        </span>
                                        <span className="text-[10px] font-medium text-gray-500 mt-1" data-id="element-4896">
                                          {entry.percentOfTotal}%
                                        </span>
                                      </div>
                                    </div>;
                      })}
                              </div>}
                          </div>

                          {codeFrequencyReport.suspectedNoise.length > 0 && <div className="mt-4 pt-3 border-t border-gray-100 flex items-start gap-2 text-[11px] text-amber-800 bg-amber-50/50 -mx-2 px-4 py-3 rounded-xl" data-id="element-4897">
                              <Info className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" data-id="element-4898" />
                              <span className="leading-relaxed" data-id="element-4899">
                                <span className="font-bold" data-id="element-4900">
                                  {codeFrequencyReport.suspectedNoise.length}
                                </span>{' '}
                                codes gemarkeerd als <em data-id="element-4901">verdacht ruis</em> —
                                samen goed voor{' '}
                                <span className="font-bold" data-id="element-4902">
                                  {codeFrequencyReport.suspectedNoise.reduce((s, e) => s + e.count, 0).toLocaleString('nl-NL')}
                                </span>{' '}
                                van{' '}
                                {codeFrequencyReport.totalIssues.toLocaleString('nl-NL')}{' '}
                                bevindingen. Overweeg severity te verlagen of
                                als info-emit te markeren.
                              </span>
                            </div>}
                        </Card>}
                  </div>
                </div>
              </motion.div>}
          </AnimatePresence>
        </section>

        {/* ZONE C: Systeem-overzicht */}
        <section className="relative" data-id="element-4903">
          <header className="flex items-start gap-4 mb-6" data-id="element-4904">
            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-slate-100 text-slate-600 text-sm font-bold border-4 border-white shrink-0 hidden sm:flex shadow-sm" data-id="element-4905">
              3
            </div>
            <div className="pt-1 w-full" data-id="element-4906">
              <button onClick={() => setIsSysteemOpen(!isSysteemOpen)} className="flex items-center justify-between w-full text-left group" data-id="element-4907">
                <div data-id="element-4908">
                  <h2 className="text-lg font-bold text-gray-900 group-hover:text-slate-600 transition-colors" data-id="element-4909">
                    Systeem-overzicht
                  </h2>
                  <p className="text-sm text-gray-500 mt-0.5" data-id="element-4910">
                    Hoe staat het systeem er in zijn totaliteit voor?
                  </p>
                </div>
                <div className="p-2 rounded-full hover:bg-gray-100 transition-colors" data-id="element-4911">
                  {isSysteemOpen ? <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-4912" /> : <ChevronRight className="w-5 h-5 text-gray-400" data-id="element-4913" />}
                </div>
              </button>
            </div>
          </header>

          <AnimatePresence initial={false} data-id="element-4914">
            {isSysteemOpen && <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} transition={{
            duration: 0.2
          }} className="sm:ml-12 overflow-hidden" data-id="element-4915">
                <div className="space-y-6 pb-2" data-id="element-4916">
                  {/* Stats — question-level counts */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-4917">
                    <StatCard label="Totaal Vragen" value={stats.total.toLocaleString('nl-NL')} icon={<FileText className="w-5 h-5" data-id="element-4919" />} color="blue" subtitle="Unieke vragen in het systeem" data-id="element-4918" />
                    <StatCard label="Geslaagd (PASS)" value={stats.pass.toLocaleString('nl-NL')} icon={<CheckCircle2 className="w-5 h-5" data-id="element-4921" />} color="emerald" subtitle={`${Math.round(stats.pass / Math.max(stats.total, 1) * 100)}% van alle vragen`} data-id="element-4920" />
                    <StatCard label="Afgekeurd (FAIL)" value={stats.fail.toLocaleString('nl-NL')} icon={<XCircle className="w-5 h-5" data-id="element-4923" />} color={stats.fail === 0 ? 'emerald' : 'red'} subtitle={`${stats.blockerCount} blocker-bevindingen`} data-id="element-4922" />
                    <StatCard label="Review Nodig" value={stats.review.toLocaleString('nl-NL')} icon={<AlertTriangle className="w-5 h-5" data-id="element-4925" />} color={stats.review === 0 ? 'emerald' : 'amber'} subtitle={`${stats.warningCount} verbeterpunten`} data-id="element-4924" />
                  </div>

                  {/* 5-level severity tiles */}
                  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5" data-id="element-4926">
                    <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4" data-id="element-4927">
                      Bevindingen per ernst
                    </h3>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-id="element-4928">
                      {[{
                    key: 'critical' as const,
                    label: 'Kritiek',
                    Icon: AlertOctagon,
                    bg: 'bg-red-50/50',
                    border: 'border-red-100',
                    text: 'text-red-700',
                    iconClass: 'text-red-600',
                    hint: 'Blocker · ernst ≥90'
                  }, {
                    key: 'blocker' as const,
                    label: 'Blocker',
                    Icon: XCircle,
                    bg: 'bg-orange-50/50',
                    border: 'border-orange-100',
                    text: 'text-orange-700',
                    iconClass: 'text-orange-600',
                    hint: 'Blocker · ernst 70-89'
                  }, {
                    key: 'review' as const,
                    label: 'Review',
                    Icon: Eye,
                    bg: 'bg-yellow-50/50',
                    border: 'border-yellow-100',
                    text: 'text-yellow-700',
                    iconClass: 'text-yellow-600',
                    hint: 'Coach · ernst ≥45'
                  }, {
                    key: 'coach' as const,
                    label: 'Coach',
                    Icon: AlertTriangle,
                    bg: 'bg-blue-50/50',
                    border: 'border-blue-100',
                    text: 'text-blue-700',
                    iconClass: 'text-blue-600',
                    hint: 'Coach · ernst <45'
                  }, {
                    key: 'info' as const,
                    label: 'Info',
                    Icon: Info,
                    bg: 'bg-slate-50/50',
                    border: 'border-slate-100',
                    text: 'text-slate-700',
                    iconClass: 'text-slate-500',
                    hint: 'Observatie'
                  }].map(tier => {
                    const count = stats.severityCounts?.[tier.key] ?? 0;
                    const TierIcon = tier.Icon;
                    return <div key={tier.key} className={`flex flex-col p-3 rounded-xl border ${tier.bg} ${tier.border} transition-transform hover:scale-[1.02]`} title={tier.hint} data-id="element-4929">
                            <TierIcon className={`w-5 h-5 mb-2 ${tier.iconClass}`} data-id="element-4930" />
                            <p className={`text-2xl font-bold leading-none ${tier.text}`} data-id="element-4931">
                              {count}
                            </p>
                            <p className={`text-[11px] font-semibold uppercase tracking-wider ${tier.text} mt-1 opacity-80`} data-id="element-4932">
                              {tier.label}
                            </p>
                          </div>;
                  })}
                    </div>
                  </div>

                  {/* Explainer legend */}
                  <div className="flex items-start gap-3 px-5 py-4 rounded-xl bg-blue-50/50 border border-blue-100" data-id="element-4933">
                    <Info className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" data-id="element-4934" />
                    <div className="text-sm text-blue-800/90 leading-relaxed" data-id="element-4935">
                      <span className="font-semibold text-blue-900" data-id="element-4936">
                        Hoe lees je deze cijfers?
                      </span>{' '}
                      <span className="font-semibold text-red-700" data-id="element-4937">
                        Afgekeurd
                      </span>{' '}
                      en{' '}
                      <span className="font-semibold text-amber-700" data-id="element-4938">
                        Review
                      </span>{' '}
                      tellen het aantal <em data-id="element-4939">vragen</em> met die status. Elke
                      vraag kan meerdere <em data-id="element-4940">bevindingen</em> hebben
                      (Kritiek/Blocker/Review/Coach/Info). De Pipeline Fasen
                      hierboven tonen het totaal aantal bevindingen per
                      controle-fase.
                    </div>
                  </div>

                  {/* System Coverage */}
                  <Card className="p-6 shadow-sm border-gray-200/60" data-id="element-4941">
                    <div className="mb-6" data-id="element-4942">
                      <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2" data-id="element-4943">
                        <Database className="w-5 h-5 text-indigo-500" data-id="element-4944" />{' '}
                        Systeemdekking
                      </h3>
                      <p className="text-xs text-gray-500 mt-1" data-id="element-4945">
                        Overzicht van beschikbare componenten en hun gebruik in
                        het vragenbestand.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8" data-id="element-4946">
                      <div className="bg-indigo-50/40 rounded-xl p-4 border border-indigo-100 text-center" data-id="element-4947">
                        <p className="text-3xl font-bold text-indigo-700" data-id="element-4948">
                          {systemStats.visualTypeCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-indigo-600/80 mt-1" data-id="element-4949">
                          Visual Types
                        </p>
                      </div>
                      <div className="bg-purple-50/40 rounded-xl p-4 border border-purple-100 text-center" data-id="element-4950">
                        <p className="text-3xl font-bold text-purple-700" data-id="element-4951">
                          {systemStats.questionTypeCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-purple-600/80 mt-1" data-id="element-4952">
                          Vraagtypes
                        </p>
                      </div>
                      <div className="bg-blue-50/40 rounded-xl p-4 border border-blue-100 text-center" data-id="element-4953">
                        <p className="text-3xl font-bold text-blue-700" data-id="element-4954">
                          {systemStats.familyCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-blue-600/80 mt-1" data-id="element-4955">
                          Families
                        </p>
                      </div>
                      <div className="bg-emerald-50/40 rounded-xl p-4 border border-emerald-100 text-center" data-id="element-4956">
                        <p className="text-3xl font-bold text-emerald-700" data-id="element-4957">
                          {systemStats.moduleCount}
                        </p>
                        <p className="text-xs font-semibold uppercase tracking-wider text-emerald-600/80 mt-1" data-id="element-4958">
                          Modules
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-8" data-id="element-4959">
                      <div data-id="element-4960">
                        <h4 className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-wider" data-id="element-4961">
                          Top 5 Vraagtypes
                        </h4>
                        <div className="space-y-2.5" data-id="element-4962">
                          {Array.from(questionTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm bg-gray-50/50 px-3 py-2 rounded-lg border border-gray-100" data-id="element-4963">
                                <span className="text-gray-700 font-mono text-xs font-medium truncate mr-2" data-id="element-4964">
                                  {type}
                                </span>
                                <span className="font-bold text-gray-900 bg-white shadow-sm border border-gray-200 px-2 py-0.5 rounded text-xs" data-id="element-4965">
                                  {count}
                                </span>
                              </div>)}
                        </div>
                      </div>
                      <div data-id="element-4966">
                        <h4 className="text-xs font-bold text-gray-400 mb-4 uppercase tracking-wider" data-id="element-4967">
                          Top 5 Visuals
                        </h4>
                        <div className="space-y-2.5" data-id="element-4968">
                          {Array.from(visualTypeDistribution.entries()).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([type, count]) => <div key={type} className="flex items-center justify-between text-sm bg-gray-50/50 px-3 py-2 rounded-lg border border-gray-100" data-id="element-4969">
                                <span className="text-gray-700 font-mono text-xs font-medium truncate mr-2" data-id="element-4970">
                                  {type}
                                </span>
                                <span className="font-bold text-gray-900 bg-white shadow-sm border border-gray-200 px-2 py-0.5 rounded text-xs" data-id="element-4971">
                                  {count}
                                </span>
                              </div>)}
                        </div>
                      </div>
                    </div>
                  </Card>
                </div>
              </motion.div>}
          </AnimatePresence>
        </section>
      </div>
    </div>;
}